import { useEffect, useState } from "react";
import { useApi } from "@/api/useApi";
import type { ReceiptDto } from "@/api/types";
import { Icon } from "@/components/Icon";
import { ReceiptViewer, type ViewerKind } from "@/components/ReceiptViewer";
import { readEmail } from "@/emailView";
import { pdfThumbnail } from "@/pdfThumbnail";

/**
 * The stored receipt, fetched with the bearer token (an <img src> cannot send one) and shown from
 * an object URL: a photo as it is, a PDF as a picture of its first page, a saved email as the top
 * of the email itself, shrunk down. Tapping a photo, PDF
 * or saved email (.eml) opens it full screen; an Outlook .msg, which the browser can't read, opens
 * in a new tab.
 */
export function ReceiptPreview({ receipt }: { receipt: ReceiptDto }) {
  const api = useApi();
  const [url, setUrl] = useState<string>();
  const [thumb, setThumb] = useState<string>();
  const [emailPage, setEmailPage] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [viewing, setViewing] = useState(false);
  const isImage = receipt.contentType.startsWith("image/");
  const isPdf = receipt.contentType === "application/pdf";
  const isEmail =
    receipt.contentType === "message/rfc822" || receipt.fileName.toLowerCase().endsWith(".eml");
  const kind: ViewerKind | undefined = isImage
    ? "photo"
    : isPdf
      ? "pdf"
      : isEmail
        ? "email"
        : undefined;

  useEffect(() => {
    const urls: string[] = [];
    let cancelled = false;
    api
      .blob(`/api/expenses/receipts/${receipt.id}`)
      .then(async (blob) => {
        if (cancelled) return;
        const full = URL.createObjectURL(blob);
        urls.push(full);
        setUrl(full);
        if (isPdf) {
          // A PDF that can't be drawn still opens; it just shows its name instead.
          const picture = await pdfThumbnail(blob).catch(() => undefined);
          if (picture && !cancelled) {
            const preview = URL.createObjectURL(picture);
            urls.push(preview);
            setThumb(preview);
          }
        }
        if (isEmail) {
          // An email that can't be read still opens; it just shows its name instead.
          const view = await readEmail(blob).catch(() => undefined);
          view?.files.forEach((file) => URL.revokeObjectURL(file.url));
          if (view && !cancelled) setEmailPage(view.page);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [api, receipt.id, isPdf, isEmail]);

  if (failed) {
    return (
      <div className="receipt-thumb missing">
        <Icon name="file" />
        <span>Receipt unavailable</span>
      </div>
    );
  }

  if (!url) return <div className="receipt-thumb skeleton" aria-label="Loading receipt" />;

  const content = isImage ? (
    <img src={url} alt={`Receipt ${receipt.fileName}`} />
  ) : thumb ? (
    <>
      <img src={thumb} alt={`First page of ${receipt.fileName}`} />
      <span className="pdf-badge">
        <Icon name="file" size={12} /> PDF
      </span>
    </>
  ) : emailPage ? (
    <>
      {/* Only a picture of the email: it takes no clicks, and nothing in it runs. */}
      <iframe
        className="email-thumb"
        title={`Start of ${receipt.fileName}`}
        sandbox=""
        srcDoc={emailPage}
        tabIndex={-1}
        aria-hidden="true"
      />
      <span className="pdf-badge">
        <Icon name="mail" size={12} /> Email
      </span>
    </>
  ) : (
    <>
      <Icon name={isEmail ? "mail" : "file"} />
      <span>{receipt.fileName}</span>
    </>
  );

  if (!kind) {
    return (
      <a
        className="receipt-thumb"
        href={url}
        target="_blank"
        rel="noreferrer"
        title={`Open ${receipt.fileName}`}
      >
        {content}
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`receipt-thumb ${thumb ? "pdf" : emailPage ? "pdf email" : ""}`}
        title={`Open ${receipt.fileName}`}
        onClick={() => setViewing(true)}
      >
        {content}
      </button>
      {viewing && (
        <ReceiptViewer
          url={url}
          fileName={receipt.fileName}
          kind={kind}
          onClose={() => setViewing(false)}
        />
      )}
    </>
  );
}
